import React from "react";
import { AlertOctagon, AlertTriangle, Check } from "react-feather";
import styled, { css } from "styled-components";

type Type = "error" | "warn" | "success" | "placeholder";

interface Props {
  warn?: string | boolean;
  error?: string | boolean;
  success?: string | boolean;
  placeholder?: string;
  className?: string;
  padBottom?: boolean;
  padTop?: boolean;
}

// Same tokens as the kit Banner; the kit one is a single-line pill, so long text wraps badly.
const Box = styled.div<{
  $type: Type;
  $padBottom?: boolean;
  $padTop?: boolean;
}>`
  margin: auto;
  width: fit-content;
  max-width: min(100%, 560px);
  box-sizing: border-box;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 15px;
  line-height: 20px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 30px;
  text-align: left;

  svg {
    flex: 0 0 auto;
    margin-top: 1.5px;
  }

  ${(p) =>
    p.$type === "error" &&
    css`
      background-color: var(--redBackground);
      color: var(--red500);
      border: 1px solid var(--redBorderOverlay);
    `}
  ${(p) =>
    p.$type === "warn" &&
    css`
      background-color: var(--orangeBackground, rgba(255, 140, 0, 0.12));
      color: var(--orange500);
      border: 1px solid var(--orangeBorder);
    `}
  ${(p) => p.$type === "success" && `color: var(--green300);`}
  ${(p) => p.$type === "placeholder" && `color: var(--dark700);`}
  ${(p) => p.$padTop && "margin-top: 20px;"}
  ${(p) => p.$padBottom && "margin-bottom: 20px;"}
`;

const Text = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  text-wrap: pretty;
`;

const Title = styled.div`
  font-weight: 600;
`;

const Body = styled.div`
  margin-top: 2px;
  font-size: 14px;
  opacity: 0.9;
`;

const LONG = 90;
const MIN_TITLE = 15;
// Sentence ends that aren't: "e.g.", "v1.", ...
const ABBREV = /\b(?:e\.g|i\.e|etc|vs|approx|v\d+)\.$/i;

/** Long multi-sentence text reads better as a title line plus a body. */
export const splitMessage = (message: string) => {
  if (message.length > LONG) {
    const end = /[.!?]\s+/g;
    let m: RegExpExecArray | null;
    while ((m = end.exec(message))) {
      const title = message.slice(0, m.index + 1);
      if (title.length >= MIN_TITLE && !ABBREV.test(title)) {
        return { title, body: message.slice(m.index! + m[0].length) };
      }
    }
  }
  return { title: message, body: "" };
};

const pick = (v: string | boolean | undefined, fallback: string) =>
  v ? (v === true ? fallback : v) : "";

/** ab-vault's Banner: the kit's look, but long messages wrap cleanly beside a top-aligned icon. */
const Banner = ({
  warn,
  error,
  success,
  placeholder,
  className,
  padBottom,
  padTop,
}: Props) => {
  const type: Type | "" = error
    ? "error"
    : warn
    ? "warn"
    : success
    ? "success"
    : placeholder
    ? "placeholder"
    : "";
  if (!type) {
    return null;
  }
  const message =
    pick(error, "Unexpected error") ||
    pick(warn, "Unsaved changes") ||
    pick(success, "Saved") ||
    (placeholder ?? "");
  const { title, body } = splitMessage(message);

  return (
    <Box
      $type={type}
      className={className}
      $padBottom={padBottom}
      $padTop={padTop}
      role={type === "error" ? "alert" : undefined}
    >
      {type === "error" ? (
        <AlertOctagon size={17} aria-hidden />
      ) : type === "warn" ? (
        <AlertTriangle size={17} aria-hidden />
      ) : type === "success" ? (
        <Check size={17} aria-hidden />
      ) : null}
      <Text>
        <Title style={body ? undefined : { fontWeight: "inherit" }}>
          {title}
        </Title>
        {body && <Body>{body}</Body>}
      </Text>
    </Box>
  );
};

export default Banner;
